'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState } from 'react';
import Image from 'next/image';
import { CourseRibbonWrapper } from '@udemy-v2/react-browse-course';
import styles from './course-lede.module.scss';
import classNames from 'classnames';
import { CourseLanguage } from '../course-languages/course-language.react-component';
import { BreadcrumbNavigation } from '../breadcrumb-navigation/breadcrumb-navigation.react-component';
import { InstructorDisplay } from '../instructor-display/instructor-display.react-component';
import { RatingDisplay } from '@/features/course-landing-page/components/rating-display/rating-display.react-component';
import { EnrollmentCount } from '@/features/course-landing-page/components/enrollment-count/enrollment-count.react-component';
import { LastUpdated } from '../last-updated/last-updated.react-component';
import { CourseCaptionDetails } from '../course-caption-details/course-caption-details.react-component';
import { useClpCourse } from '../../context/clp-course-provider.react-component';
import { CoursePreviewEntryPointWrapper } from '@/features/course-landing-page/components/course-preview-entry-point/course-preview-entry-point.react-component';
import { AlternateRedirect } from '../alternate-redirect/alternate-redirect.react-component';
import { useCommonData } from '@udemy-v2/common-data';
import { PositionMarkerContainer } from '@/components/position-marker';
import { CLPResourceContextMenu } from './clp-resource-context-menu.react-component';
import { GoogleCertificateRibbon } from '../google-certificate-ribbon/google-certificate-ribbon.react-component';
import { useCourseCareerAccelerator } from '../../context/career-accelerator-provider.react-component';
import { useI18n } from '@udemy-v2/i18n';
const PREVIEW_UI_REGION = 'introductionAsset';
export interface CourseLedeProps {
  className?: string;
  hideRating?: boolean;
  hideEnrollments?: boolean;
}

/**
 * Displays the main course header information on CLP
 */
export const CourseLede: React.FC<CourseLedeProps> = t0 => {
  const $ = _c(73);
  const {
    className,
    hideRating: t1,
    hideEnrollments: t2
  } = t0;
  const hideRating = t1 === undefined ? false : t1;
  const hideEnrollments = t2 === undefined ? false : t2;
  const {
    gettext
  } = useI18n();
  const [isCaptionsExpanded, setIsCaptionsExpanded] = useState(false);
  const course = useClpCourse();
  const commonData = useCommonData();
  const hasOrganization = !!commonData.organization;
  const isCommonDataReady = commonData.page?.isLoading === false;
  const isGrowWithGoogleCourse = !!course.contentCollectionInfo?.isInGooglePartnerCollection;
  const courseCareerAccelerator = useCourseCareerAccelerator();
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = expanded => {
      setIsCaptionsExpanded(expanded);
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const handleCaptionsExpandChange = t3;
  const primaryCategory = course.categories && course.categories.length > 0 ? course.categories[0] : undefined;
  let t4;
  if ($[1] !== className) {
    t4 = classNames(styles["course-lede"], className);
    $[1] = className;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== course.primaryTopic || $[4] !== gettext || $[5] !== hasOrganization || $[6] !== primaryCategory) {
    t5 = !hasOrganization && <BreadcrumbNavigation ariaLabel={gettext("Course topics")} primaryCategory={primaryCategory} primaryTopic={course.primaryTopic} />;
    $[3] = course.primaryTopic;
    $[4] = gettext;
    $[5] = hasOrganization;
    $[6] = primaryCategory;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <AlternateRedirect />;
    t7 = <CoursePreviewEntryPointWrapper uiRegion={PREVIEW_UI_REGION} hideWhenLedeOutOfView={false} />;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  let t8;
  if ($[10] !== isGrowWithGoogleCourse) {
    t8 = isGrowWithGoogleCourse && <Image src="https://frontends.udemycdn.com/staticx/udemy/images/grow-with-google/google-logo.webp" alt="Google Logo" width={98} height={32} className={styles["google-logo"]} />;
    $[10] = isGrowWithGoogleCourse;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = classNames(styles.title, "ud-heading-xxl");
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== course.title) {
    t10 = <h1 className={t9}>{course.title}</h1>;
    $[13] = course.title;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== course.id || $[16] !== courseCareerAccelerator || $[17] !== isGrowWithGoogleCourse) {
    t11 = isGrowWithGoogleCourse && <><GoogleCertificateRibbon courseCareerAccelerator={courseCareerAccelerator} firstCourseId={course.id} /></>;
    $[15] = course.id;
    $[16] = courseCareerAccelerator;
    $[17] = isGrowWithGoogleCourse;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== course.headline) {
    t12 = course.headline && <div className={classNames(styles.headline, "ud-text-lg")}>{course.headline}</div>;
    $[19] = course.headline;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== course || $[22] !== hasOrganization || $[23] !== isCommonDataReady || $[24] !== isGrowWithGoogleCourse) {
    t13 = isCommonDataReady && !hasOrganization && !isGrowWithGoogleCourse && <CourseRibbonWrapper course={course} numberOfBadgesToShow={2} />;
    $[21] = course;
    $[22] = hasOrganization;
    $[23] = isCommonDataReady;
    $[24] = isGrowWithGoogleCourse;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== course.rating || $[27] !== hideRating) {
    t14 = !hideRating && course.rating?.average && <RatingDisplay average={course.rating.average} count={course.rating.count} />;
    $[26] = course.rating;
    $[27] = hideRating;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t14) {
    t15 = <div>{t14}</div>;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== course.enrollments || $[32] !== hideEnrollments) {
    t16 = !hideEnrollments && course.enrollments?.roundedCount && <EnrollmentCount count={course.enrollments.count} />;
    $[31] = course.enrollments;
    $[32] = hideEnrollments;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16) {
    t17 = <div>{t16}</div>;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t13 || $[37] !== t15 || $[38] !== t17) {
    t18 = <div className={styles["metadata-container"]}>{t13}{t15}{t17}</div>;
    $[36] = t13;
    $[37] = t15;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== course.instructors || $[41] !== isGrowWithGoogleCourse) {
    t19 = <div className={styles["instructor-section"]}><InstructorDisplay instructors={course.instructors} isAGwGCourse={isGrowWithGoogleCourse} /></div>;
    $[40] = course.instructors;
    $[41] = isGrowWithGoogleCourse;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== isCaptionsExpanded) {
    t20 = classNames(styles["last-updated-languages-container"], {
      [styles["expanded-captions"]]: isCaptionsExpanded
    });
    $[43] = isCaptionsExpanded;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <LastUpdated />;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== course.locale) {
    t22 = <CourseLanguage courseLocale={course.locale} />;
    $[46] = course.locale;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== course.captions) {
    t23 = course.captions?.map(_temp) || [];
    $[48] = course.captions;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== isCaptionsExpanded || $[51] !== t23) {
    t24 = <CourseCaptionDetails captionedLanguages={t23} isExpanded={isCaptionsExpanded} onExpandChange={handleCaptionsExpandChange} />;
    $[50] = isCaptionsExpanded;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t20 || $[54] !== t22 || $[55] !== t24) {
    t25 = <div className={t20}>{t21}{t22}{t24}</div>;
    $[53] = t20;
    $[54] = t22;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== course || $[58] !== hasOrganization) {
    t26 = hasOrganization && <CLPResourceContextMenu course={course} />;
    $[57] = course;
    $[58] = hasOrganization;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t10 || $[61] !== t11 || $[62] !== t12 || $[63] !== t18 || $[64] !== t19 || $[65] !== t25 || $[66] !== t26 || $[67] !== t5 || $[68] !== t8) {
    t27 = <div className={styles.container}>{t5}{t6}{t7}{t8}{t10}{t11}{t12}{t18}{t19}{t25}{t26}</div>;
    $[60] = t10;
    $[61] = t11;
    $[62] = t12;
    $[63] = t18;
    $[64] = t19;
    $[65] = t25;
    $[66] = t26;
    $[67] = t5;
    $[68] = t8;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== t27 || $[71] !== t4) {
    t28 = <PositionMarkerContainer threshold={0.1}><section className={t4}>{t27}</section></PositionMarkerContainer>;
    $[70] = t27;
    $[71] = t4;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  return t28;
};
function _temp(caption) {
  return caption;
}